/**
 * Bridge between the API client and the dialog that asks staff to confirm a
 * visit action the API refused on a rule they may knowingly override.
 *
 * The API answers 409 with a code, the client asks once, and repeats the
 * request with the override when staff say yes. Visits are checked in, started and closed from the queue, the calendar,
 * the list and the visit page, so the question lives here rather than in each
 * of them. `VisitConfirmHost` supplies the asker while it is mounted.
 */
export interface VisitRefusal {
  code: VisitConfirmCode;
  /**
   * VISIT_NOT_TODAY: when the visit is booked for. PATIENT_DOUBLE_BOOKED:
   * when the patient's other appointment is.
   */
  scheduledAt?: string;
  /** PATIENT_DOUBLE_BOOKED: the branch of the other appointment. */
  branchName?: string | null;
  /** OUTSIDE_WORKING_HOURS: which rule the time breaks. */
  reason?: 'BRANCH_CLOSED' | 'OUTSIDE_HOURS' | 'DOCTOR_NOT_WORKING';
  /** OUTSIDE_WORKING_HOURS with BRANCH_CLOSED: the closure's own note. */
  closureReason?: string | null;
  /** PROCEDURES_PENDING: the procedures nobody finished. */
  procedures?: Array<{ id: string; name: string | null; status?: string }>;
  count?: number;
}

/**
 * The refusals that are questions, and the request field that answers "yes".
 *
 * - `VISIT_NOT_TODAY`: checking in or starting a visit booked for a later
 *   day. Saying yes moves the visit to now.
 * - `PROCEDURES_PENDING`: closing a visit with unfinished procedures.
 * - `PATIENT_DOUBLE_BOOKED`: booking a time the patient already has an
 *   appointment at, in any branch.
 * - `OUTSIDE_WORKING_HOURS`: booking when the branch is closed, outside its
 *   opening hours, or when the doctor is not working.
 */
export const VISIT_CONFIRM_OVERRIDES = {
  VISIT_NOT_TODAY: 'moveToToday',
  PROCEDURES_PENDING: 'allowPendingProcedures',
  PATIENT_DOUBLE_BOOKED: 'allowPatientOverlap',
  OUTSIDE_WORKING_HOURS: 'allowOutsideHours',
} as const;

export type VisitConfirmCode = keyof typeof VISIT_CONFIRM_OVERRIDES;

export function isVisitConfirmCode(code: unknown): code is VisitConfirmCode {
  return typeof code === 'string' && code in VISIT_CONFIRM_OVERRIDES;
}

type Asker = (refusal: VisitRefusal) => Promise<boolean>;

let asker: Asker | null = null;

export function registerVisitConfirmAsker(next: Asker): () => void {
  asker = next;
  return () => {
    if (asker === next) asker = null;
  };
}

/** Resolves false when nothing is mounted to ask, so the 409 surfaces as is. */
export function askVisitConfirm(refusal: VisitRefusal): Promise<boolean> {
  return asker ? asker(refusal) : Promise.resolve(false);
}

/**
 * Refusals answered with a reason rather than a yes: the API wants to know
 * why, and keeps it with the change. The value is the request field it goes
 * in.
 *
 * - `DOCTOR_CHANGE_REASON_REQUIRED`: moving a finished visit to another
 *   doctor (it moves their commission).
 */
export const VISIT_REASON_FIELDS = {
  DOCTOR_CHANGE_REASON_REQUIRED: 'doctorChangeReason',
} as const;

export type VisitReasonCode = keyof typeof VISIT_REASON_FIELDS;

export function isVisitReasonCode(code: unknown): code is VisitReasonCode {
  return typeof code === 'string' && code in VISIT_REASON_FIELDS;
}

type ReasonAsker = (code: VisitReasonCode) => Promise<string | null>;

let reasonAsker: ReasonAsker | null = null;

export function registerVisitReasonAsker(next: ReasonAsker): () => void {
  reasonAsker = next;
  return () => {
    if (reasonAsker === next) reasonAsker = null;
  };
}

/** Resolves null when nothing is mounted to ask, or staff cancel. */
export function askVisitReason(code: VisitReasonCode): Promise<string | null> {
  return reasonAsker ? reasonAsker(code) : Promise.resolve(null);
}
